import { Component, computed, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { NotificationsService } from '@services/notifications.service';
import { UserService } from '@services/user.service';
import { CommonButtonComponent } from '@shared/elements/common-button/button.component';
import { ImageComponent } from '@shared/elements/image/image.component';
import { TextFieldComponent } from '@shared/elements/text-field/text-field.component';
import { peekPendingCheckout } from '@shared/helpers/event-purchase.helper';
import { saveMandatoryUserInfoToStorage } from '@shared/helpers/system.helper';
import { LoginData, LoginInfo } from '@shared/models/model';
import { LocalStorageService } from '../../services/local-storage.service';

@Component({
  selector: 'login',
  standalone: true,
  imports: [ReactiveFormsModule, CommonButtonComponent, TextFieldComponent, ImageComponent, RouterLink],
  templateUrl: './login.component.html',
})
export class LoginComponent {
  private readonly storage = inject(LocalStorageService);
  private readonly userService = inject(UserService);
  private readonly notificationService = inject(NotificationsService);
  readonly router = inject(Router);

  readonly loggedIn = computed(() => this.storage.isLoggedIn());
  readonly logoId = computed(() => this.storage.logoId());

  loginForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email, Validators.maxLength(120)]),
    password: new FormControl('', [Validators.required, Validators.minLength(4), Validators.maxLength(120)]),
  });

  isLoginForm = true;

  constructor() {
    if (this.storage.isLoggedIn()) {
      this.router.navigate(['/admin/dashboard']);
    }
  }

  get email() {
    return this.loginForm.get('email') as FormControl;
  }

  get password() {
    return this.loginForm.get('password') as FormControl;
  }

  submit() {
    const loginData = {
      email: this.loginForm.value['email'],
      password: this.loginForm.value['password'],
      domainName: '',
    } as LoginData;

    localStorage.removeItem('MODE');
    this.storage.previewUserScreens.set(false);

    this.userService.login(loginData).subscribe((res) => {
      this.confirmSuccessfullLoginAndProceed(res.data);
    });
  }

  confirmSuccessfullLoginAndProceed(info: LoginInfo) {
    if (!info || !info?.jwt) {
      this.storage.changeLoggedIn(false);
      localStorage.clear();
      return;
    }

    saveMandatoryUserInfoToStorage(info);
    this.storage.userInfo.set(info.userInfo);
    this.storage.changeLoggedIn(true);
    this.navigateToStartPageAndUpdateTheme(info.userInfo.userRole.name);
  }

  navigateToStartPageAndUpdateTheme(role: string) {
    if (role === 'SUPER_ADMIN' || role === 'DOMAIN_ADMIN') {
      this.storage.toggleTheme('dark');
      this.router.navigate(['/admin/dashboard']);
    } else if (role) {
      const pending = peekPendingCheckout();
      this.router.navigateByUrl(pending?.returnUrl ?? '/home');
    }
  }

  forgotPassword() {
    this.isLoginForm = false;
  }

  sendRequestToResetPassword() {
    this.userService.resetForgottenPassword(this.email.value).subscribe((res) => {
      if (res.success) {
        this.notificationService.info(`You will receive an email`, 'Please follow the instructions', 20000);
      } else {
        this.notificationService.error(`Something went wrong`, 'Please reach out to our support team', 20000);
      }
    });
  }
}
